import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { ArrowRight, TrendingUp, Shield, Zap, Users, Star, BarChart3, Clock } from "lucide-react";

export default function Landing() {
  const navigate = useNavigate();
  const [totalProfit, setTotalProfit] = useState(0);

  useEffect(() => {
    // Animated counter
    let start = 0;
    const end = 284562;
    const duration = 2000;
    const increment = end / (duration / 16);
    
    const timer = setInterval(() => {
      start += increment;
      if (start >= end) {
        setTotalProfit(end);
        clearInterval(timer);
      } else {
        setTotalProfit(Math.floor(start));
      }
    }, 16);

    return () => clearInterval(timer);
  }, []);

  const liveTrades = [
    { pair: "EUR/USD", profit: "+$245", color: "text-[#00E676]" },
    { pair: "GBP/USD", profit: "+$189", color: "text-[#00E676]" },
    { pair: "USD/JPY", profit: "-$45", color: "text-[#FF3D00]" },
    { pair: "AUD/USD", profit: "+$312", color: "text-[#00E676]" },
    { pair: "USD/CHF", profit: "+$156", color: "text-[#00E676]" },
  ];

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white">
      <Navbar />
      
      {/* Hero Section */}
      <section className="pt-24 md:pt-32 pb-12 md:pb-20 px-4 md:px-8 relative overflow-hidden">
        {/* Animated Background */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-20 left-10 w-64 h-64 bg-[#00E676] rounded-full blur-3xl"></div>
          <div className="absolute bottom-20 right-10 w-96 h-96 bg-[#007AFF] rounded-full blur-3xl"></div>
        </div>

        <div className="max-w-7xl mx-auto relative z-10">
          <div className="text-center space-y-8">


            {/* Trust Badge */}
            <div className="inline-flex items-center gap-2 bg-[#00E676]/10 border border-[#00E676]/30 rounded-full px-6 py-2">
              <Star className="w-4 h-4 text-[#00E676] fill-[#00E676]" />
              <span className="text-sm font-medium text-[#00E676]">4.8★ Rated by 500+ Traders</span>
            </div>

            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold leading-tight mt-6">
              <span className="text-2xl sm:text-3xl text-[#A1A1AA] font-normal tracking-wide block mb-3">Kairosis Capital</span>
              <span className="text-[#00E676] tracking-tight">Alphabot</span>
              <br />
              <span className="text-3xl sm:text-4xl lg:text-5xl mt-2 block">Trades While You Sleep</span>
            </h1>

            <p className="text-xl text-[#A1A1AA] max-w-3xl mx-auto">
              Fully automated MT5 trading bot that executes profitable trades 24/7. 
              No emotions. No fatigue. Just consistent returns.
            </p>

            {/* Animated Profit Counter */}
            <div data-testid="profit-counter" className="py-8">
              <p className="text-sm text-[#A1A1AA] mb-2">Total Profit Generated</p>
              <p className="text-3xl sm:text-5xl font-bold font-data text-[#00E676]">
                ${totalProfit.toLocaleString()}
              </p>
            </div>

            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button
                data-testid="hero-get-access-btn"
                onClick={() => navigate('/pricing')}
                size="lg"
                className="bg-[#00E676] hover:bg-[#00C853] text-black rounded-md px-12 text-lg btn-glow active:scale-95"
              >
                Get Access Now
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
              <Button
                data-testid="hero-view-performance-btn"
                onClick={() => navigate('/performance')}
                size="lg"
                variant="outline"
                className="border-[#27272A] hover:border-[#00E676] text-white rounded-md px-12 text-lg"
              >
                View Performance
              </Button>
            </div>

            {/* Trust Stats */}
            <div data-testid="trust-stats" className="grid grid-cols-1 sm:grid-cols-3 gap-6 pt-12 max-w-3xl mx-auto">
              <div className="bg-[#111111] border border-[#27272A] rounded-lg p-6 card-hover">
                <p className="text-3xl font-bold font-data text-[#00E676]">10,000+</p>
                <p className="text-sm text-[#A1A1AA] mt-2">Trades Executed</p>
              </div>
              <div className="bg-[#111111] border border-[#27272A] rounded-lg p-6 card-hover">
                <p className="text-3xl font-bold font-data text-[#00E676]">500+</p>
                <p className="text-sm text-[#A1A1AA] mt-2">Active Users</p>
              </div>
              <div className="bg-[#111111] border border-[#27272A] rounded-lg p-6 card-hover">
                <p className="text-3xl font-bold font-data text-[#00E676]">72.4%</p>
                <p className="text-sm text-[#A1A1AA] mt-2">Average Win Rate</p>
              </div>
            </div>
          </div>
        </div>

        {/* Live Ticker */}
        <div className="mt-16 overflow-hidden border-y border-[#27272A] bg-[#111111] py-4 flex">
          <div data-testid="live-ticker" className="flex gap-12 animate-ticker whitespace-nowrap pr-12 w-max hover:[animation-play-state:paused]">
            {[...liveTrades, ...liveTrades, ...liveTrades, ...liveTrades, ...liveTrades, ...liveTrades, ...liveTrades, ...liveTrades, ...liveTrades, ...liveTrades].map((trade, idx) => (
              <div key={idx} className="flex items-center gap-3">
                <span className="text-[#A1A1AA] font-medium">{trade.pair}</span>
                <span className={`font-data font-bold ${trade.color}`}>{trade.profit}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-12 md:py-20 px-4 md:px-8 bg-[#111111]">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold mb-4">Why Traders Choose Alphabot</h2>
            <p className="text-[#A1A1AA] text-lg">Premium features that give you an edge in the markets</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <div data-testid="feature-automated" className="bg-[#161616] border border-[#27272A] rounded-lg p-6 card-hover">
              <div className="w-12 h-12 bg-[#00E676]/10 rounded-lg flex items-center justify-center mb-4">
                <Zap className="w-6 h-6 text-[#00E676]" />
              </div>
              <h3 className="text-xl font-bold mb-3">100% Automated</h3>
              <p className="text-[#A1A1AA] text-sm">
                Set it and forget it. The bot trades 24/7 without any manual intervention.
              </p>
            </div>

            <div data-testid="feature-proven" className="bg-[#161616] border border-[#27272A] rounded-lg p-6 card-hover">
              <div className="w-12 h-12 bg-[#007AFF]/10 rounded-lg flex items-center justify-center mb-4">
                <BarChart3 className="w-6 h-6 text-[#007AFF]" />
              </div>
              <h3 className="text-xl font-bold mb-3">Proven Strategy</h3>
              <p className="text-[#A1A1AA] text-sm">
                Backtested on 5 years of data with consistent profitability across market conditions.
              </p>
            </div>

            <div data-testid="feature-risk" className="bg-[#161616] border border-[#27272A] rounded-lg p-6 card-hover">
              <div className="w-12 h-12 bg-[#FFC107]/10 rounded-lg flex items-center justify-center mb-4">
                <Shield className="w-6 h-6 text-[#FFC107]" />
              </div>
              <h3 className="text-xl font-bold mb-3">Risk Management</h3>
              <p className="text-[#A1A1AA] text-sm">
                Built-in stop-loss, take-profit, and max drawdown protection to preserve capital.
              </p>
            </div>

            <div data-testid="feature-support" className="bg-[#161616] border border-[#27272A] rounded-lg p-6 card-hover">
              <div className="w-12 h-12 bg-[#00E676]/10 rounded-lg flex items-center justify-center mb-4">
                <Users className="w-6 h-6 text-[#00E676]" />
              </div>
              <h3 className="text-xl font-bold mb-3">24/7 Support</h3>
              <p className="text-[#A1A1AA] text-sm">
                Dedicated support team and active community to help you maximize results.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-12 md:py-20 px-4 md:px-8">
        <div className="max-w-4xl mx-auto text-center space-y-6 md:space-y-8">
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold">
            Ready to Automate Your Trading?
          </h2>
          <p className="text-lg text-[#A1A1AA]">
            Join 500+ traders who are already profiting with Alphabot
          </p>
          <Button
            data-testid="cta-get-started-btn"
            onClick={() => navigate('/pricing')}
            size="lg"
            className="bg-[#00E676] hover:bg-[#00C853] text-black rounded-md px-12 text-lg btn-glow active:scale-95"
          >
            Get Started Today
            <ArrowRight className="ml-2 w-5 h-5" />
          </Button>
        </div>
      </section>

      <Footer />
    </div>
  );
}
